/**
 * A PowerPoint deck of pictures: a slide per picture, on A4 landscape, the
 * picture fitted and centred with the PDF's margin, so the deck prints as the
 * PDF does. The parts are the least PowerPoint, Keynote and Google Slides
 * open without repair: one master, one blank layout, a theme, and the slides.
 */
import { zip } from "./zip";

export interface Slide {
  png: Uint8Array;
  /** In any unit: only the proportion is used. */
  width: number;
  height: number;
  /** The picture's alternative text. */
  description: string;
}

/** English Metric Units per millimetre. */
const EMU_PER_MM = 36_000;
/** A4 landscape, as PowerPoint's own "A4 Paper" size. */
export const SLIDE = { width: 297 * EMU_PER_MM, height: 210 * EMU_PER_MM };
const MARGIN = 10 * EMU_PER_MM;

const NS =
  'xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" ' +
  'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ' +
  'xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main"';
const XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const PML = "application/vnd.openxmlformats-officedocument.presentationml";

function escapeXml(text: string): string {
  return text.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" })[c]!,
  );
}

function relationships(rels: readonly { id: string; type: string; target: string }[]): string {
  return `${XML}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${rels
    .map((rel) => `<Relationship Id="${rel.id}" Type="${rel.type.startsWith("http") ? rel.type : `${REL}/${rel.type}`}" Target="${rel.target}"/>`)
    .join("")}</Relationships>`;
}

const EMPTY_TREE =
  '<p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>' +
  '<p:grpSpPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/><a:chOff x="0" y="0"/><a:chExt cx="0" cy="0"/></a:xfrm></p:grpSpPr>';

/** Where a picture sits: as large as the area inside the margin allows, centred. */
export function placement(slide: Pick<Slide, "width" | "height">) {
  const area = { width: SLIDE.width - 2 * MARGIN, height: SLIDE.height - 2 * MARGIN };
  const scale = Math.min(area.width / slide.width, area.height / slide.height);
  const cx = Math.round(slide.width * scale);
  const cy = Math.round(slide.height * scale);
  return { x: Math.round((SLIDE.width - cx) / 2), y: Math.round((SLIDE.height - cy) / 2), cx, cy };
}

function slideXml(slide: Slide, index: number): string {
  const { x, y, cx, cy } = placement(slide);
  return `${XML}<p:sld ${NS}><p:cSld><p:spTree>${EMPTY_TREE}<p:pic><p:nvPicPr><p:cNvPr id="2" name="Picture ${index + 1}" descr="${escapeXml(slide.description)}"/><p:cNvPicPr><a:picLocks noChangeAspect="1"/></p:cNvPicPr><p:nvPr/></p:nvPicPr><p:blipFill><a:blip r:embed="rId2"/><a:stretch><a:fillRect/></a:stretch></p:blipFill><p:spPr><a:xfrm><a:off x="${x}" y="${y}"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></p:spPr></p:pic></p:spTree></p:cSld><p:clrMapOvr><a:masterClrMapping/></p:clrMapOvr></p:sld>`;
}

const MASTER = `${XML}<p:sldMaster ${NS}><p:cSld><p:bg><p:bgRef idx="1001"><a:schemeClr val="bg1"/></p:bgRef></p:bg><p:spTree>${EMPTY_TREE}</p:spTree></p:cSld><p:clrMap bg1="lt1" tx1="dk1" bg2="lt2" tx2="dk2" accent1="accent1" accent2="accent2" accent3="accent3" accent4="accent4" accent5="accent5" accent6="accent6" hlink="hlink" folHlink="folHlink"/><p:sldLayoutIdLst><p:sldLayoutId id="2147483649" r:id="rId1"/></p:sldLayoutIdLst><p:txStyles><p:titleStyle/><p:bodyStyle/><p:otherStyle/></p:txStyles></p:sldMaster>`;

const LAYOUT = `${XML}<p:sldLayout ${NS} type="blank" preserve="1"><p:cSld name="Blank"><p:spTree>${EMPTY_TREE}</p:spTree></p:cSld><p:clrMapOvr><a:masterClrMapping/></p:clrMapOvr></p:sldLayout>`;

const colour = (name: string, hex: string) => `<a:${name}><a:srgbClr val="${hex}"/></a:${name}>`;
const THEME_XML = `${XML}<a:theme xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" name="Fluidea"><a:themeElements><a:clrScheme name="Fluidea"><a:dk1><a:sysClr val="windowText" lastClr="000000"/></a:dk1><a:lt1><a:sysClr val="window" lastClr="FFFFFF"/></a:lt1>${colour("dk2", "1F2A33")}${colour("lt2", "EEF2F5")}${colour("accent1", "3F8FD2")}${colour("accent2", "2FA38C")}${colour("accent3", "9A67CF")}${colour("accent4", "D08A3C")}${colour("accent5", "C9567A")}${colour("accent6", "E8894A")}${colour("hlink", "3F8FD2")}${colour("folHlink", "9A67CF")}</a:clrScheme><a:fontScheme name="Fluidea"><a:majorFont><a:latin typeface="Calibri"/><a:ea typeface=""/><a:cs typeface=""/></a:majorFont><a:minorFont><a:latin typeface="Calibri"/><a:ea typeface=""/><a:cs typeface=""/></a:minorFont></a:fontScheme><a:fmtScheme name="Fluidea"><a:fillStyleLst><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:fillStyleLst><a:lnStyleLst><a:ln w="6350"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:ln><a:ln w="12700"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:ln><a:ln w="19050"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:ln></a:lnStyleLst><a:effectStyleLst><a:effectStyle><a:effectLst/></a:effectStyle><a:effectStyle><a:effectLst/></a:effectStyle><a:effectStyle><a:effectLst/></a:effectStyle></a:effectStyleLst><a:bgFillStyleLst><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:bgFillStyleLst></a:fmtScheme></a:themeElements></a:theme>`;

/** The deck as a .pptx file's bytes. */
export function pptx(slides: readonly Slide[], title: string, when = new Date()): Uint8Array<ArrayBuffer> {
  const count = slides.length;
  const slideIds = slides.map((_, i) => `<p:sldId id="${256 + i}" r:id="rId${i + 2}"/>`).join("");
  const presentation = `${XML}<p:presentation ${NS} saveSubsetFonts="1"><p:sldMasterIdLst><p:sldMasterId id="2147483648" r:id="rId1"/></p:sldMasterIdLst><p:sldIdLst>${slideIds}</p:sldIdLst><p:sldSz cx="${SLIDE.width}" cy="${SLIDE.height}" type="A4"/><p:notesSz cx="${SLIDE.height}" cy="${SLIDE.width}"/></p:presentation>`;
  const after = count + 2;
  const presentationRels = relationships([
    { id: "rId1", type: "slideMaster", target: "slideMasters/slideMaster1.xml" },
    ...slides.map((_, i) => ({ id: `rId${i + 2}`, type: "slide", target: `slides/slide${i + 1}.xml` })),
    { id: `rId${after}`, type: "presProps", target: "presProps.xml" },
    { id: `rId${after + 1}`, type: "viewProps", target: "viewProps.xml" },
    { id: `rId${after + 2}`, type: "theme", target: "theme/theme1.xml" },
    { id: `rId${after + 3}`, type: "tableStyles", target: "tableStyles.xml" },
  ]);
  const override = (part: string, type: string) => `<Override PartName="/${part}" ContentType="${type}"/>`;
  const contentTypes = `${XML}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Default Extension="png" ContentType="image/png"/>${[
    override("ppt/presentation.xml", `${PML}.presentation.main+xml`),
    override("ppt/slideMasters/slideMaster1.xml", `${PML}.slideMaster+xml`),
    override("ppt/slideLayouts/slideLayout1.xml", `${PML}.slideLayout+xml`),
    ...slides.map((_, i) => override(`ppt/slides/slide${i + 1}.xml`, `${PML}.slide+xml`)),
    override("ppt/theme/theme1.xml", "application/vnd.openxmlformats-officedocument.theme+xml"),
    override("ppt/presProps.xml", `${PML}.presProps+xml`),
    override("ppt/viewProps.xml", `${PML}.viewProps+xml`),
    override("ppt/tableStyles.xml", `${PML}.tableStyles+xml`),
    override("docProps/core.xml", "application/vnd.openxmlformats-package.core-properties+xml"),
    override("docProps/app.xml", "application/vnd.openxmlformats-officedocument.extended-properties+xml"),
  ].join("")}</Types>`;
  const iso = when.toISOString().replace(/\.\d+Z$/, "Z");
  const core = `${XML}<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><dc:title>${escapeXml(title)}</dc:title><dc:creator>Fluidea</dc:creator><dcterms:created xsi:type="dcterms:W3CDTF">${iso}</dcterms:created><dcterms:modified xsi:type="dcterms:W3CDTF">${iso}</dcterms:modified></cp:coreProperties>`;
  const app = `${XML}<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"><Application>Fluidea</Application><Slides>${count}</Slides></Properties>`;

  return zip(
    [
      { path: "[Content_Types].xml", data: contentTypes },
      {
        path: "_rels/.rels",
        data: relationships([
          { id: "rId1", type: "officeDocument", target: "ppt/presentation.xml" },
          {
            id: "rId2",
            type: "http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties",
            target: "docProps/core.xml",
          },
          { id: "rId3", type: "extended-properties", target: "docProps/app.xml" },
        ]),
      },
      { path: "docProps/core.xml", data: core },
      { path: "docProps/app.xml", data: app },
      { path: "ppt/presentation.xml", data: presentation },
      { path: "ppt/_rels/presentation.xml.rels", data: presentationRels },
      { path: "ppt/presProps.xml", data: `${XML}<p:presentationPr ${NS}/>` },
      {
        path: "ppt/viewProps.xml",
        data: `${XML}<p:viewPr ${NS}><p:slideViewPr><p:cSldViewPr><p:cViewPr varScale="1"><p:scale><a:sx n="100" d="100"/><a:sy n="100" d="100"/></p:scale><p:origin x="0" y="0"/></p:cViewPr></p:cSldViewPr></p:slideViewPr><p:gridSpacing cx="72008" cy="72008"/></p:viewPr>`,
      },
      {
        path: "ppt/tableStyles.xml",
        data: `${XML}<a:tblStyleLst xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" def="{5C22544A-7EE6-4342-B048-85BDC9FD1C3A}"/>`,
      },
      { path: "ppt/theme/theme1.xml", data: THEME_XML },
      { path: "ppt/slideMasters/slideMaster1.xml", data: MASTER },
      {
        path: "ppt/slideMasters/_rels/slideMaster1.xml.rels",
        data: relationships([
          { id: "rId1", type: "slideLayout", target: "../slideLayouts/slideLayout1.xml" },
          { id: "rId2", type: "theme", target: "../theme/theme1.xml" },
        ]),
      },
      { path: "ppt/slideLayouts/slideLayout1.xml", data: LAYOUT },
      {
        path: "ppt/slideLayouts/_rels/slideLayout1.xml.rels",
        data: relationships([{ id: "rId1", type: "slideMaster", target: "../slideMasters/slideMaster1.xml" }]),
      },
      ...slides.flatMap((slide, i) => [
        { path: `ppt/slides/slide${i + 1}.xml`, data: slideXml(slide, i) },
        {
          path: `ppt/slides/_rels/slide${i + 1}.xml.rels`,
          data: relationships([
            { id: "rId1", type: "slideLayout", target: "../slideLayouts/slideLayout1.xml" },
            { id: "rId2", type: "image", target: `../media/image${i + 1}.png` },
          ]),
        },
        { path: `ppt/media/image${i + 1}.png`, data: slide.png },
      ]),
    ],
    when,
  );
}
